import { SVGProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { RiArrowDownSLine, RiArrowUpSLine, RiCodeLine } from 'react-icons/ri'
import { Tooltip } from '@clickguard/design-system'
import { GTableColumn, GTableHeaderProps } from '../types'
import { StickyEdgeShadow } from '../sticky-edge-shadow'

// The DS TableHead sort icons, copied from @clickguard/design-system. The
// package exports the TableHead component but not these icons, and the ds
// variant must render the exact same glyphs.
const ChevronUp = (props: SVGProps<SVGSVGElement>) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={24}
		height={24}
		fill="none"
		viewBox="0 0 24 24"
		{...props}
	>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="m18 15-6-6-6 6"
		/>
	</svg>
)

const ChevronDown = (props: SVGProps<SVGSVGElement>) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={24}
		height={24}
		fill="none"
		viewBox="0 0 24 24"
		{...props}
	>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="m6 9 6 6 6-6"
		/>
	</svg>
)

const ChevronsUpDown = (props: SVGProps<SVGSVGElement>) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={24}
		height={24}
		fill="none"
		viewBox="0 0 24 24"
		{...props}
	>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="m7 15 5 5 5-5M7 9l5-5 5 5"
		/>
	</svg>
)

// The untitled-ui InfoCircle glyph the DS CustomRules recipe renders. Same
// inlining reason as the chevrons above: the DS does not export its icons.
const InfoCircle = (props: SVGProps<SVGSVGElement>) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={24}
		height={24}
		fill="none"
		viewBox="0 0 24 24"
		{...props}
	>
		<path
			stroke="currentColor"
			strokeLinecap="round"
			strokeLinejoin="round"
			strokeWidth={2}
			d="M12 16v-4m0-4h.01M22 12c0 5.5228-4.4772 10-10 10S2 17.5228 2 12 6.4772 2 12 2s10 4.4772 10 10"
		/>
	</svg>
)

export const TableHeader = <T,>(props: GTableHeaderProps<T>) => {
	const {
		column,
		reactColumn,
		sort,
		onSort,
		isScrolling,
		isAtEnd = true,
		variant
	} = props
	const isDs = variant === 'ds'

	// The ds variant cycles like the DS Sortable story: asc, desc, then back
	// to unsorted (an empty key). The default variant keeps its two-state
	// toggle, because its consumers may not accept an empty sort key.
	const raiseSort = (column: GTableColumn<T>) => {
		if (onSort && column.sortKey) {
			if (column.sortKey === sort) return onSort(`-${column.sortKey}`)
			if (isDs && `-${column.sortKey}` === sort) return onSort('')
			onSort(column.sortKey)
		}
	}
	const renderSortIcon = (column: GTableColumn<T>) => {
		if (`-${column.sortKey}` !== sort && column.sortKey !== sort)
			return <RiCodeLine className="h-3 w-3 rotate-90" />
		if (sort === column.sortKey) return <RiArrowUpSLine className="h-3 w-3" />
		if (sort === `-${column.sortKey}`)
			return <RiArrowDownSLine className="h-3 w-3" />
		return <RiCodeLine className="h-3 w-3 rotate-90" />
	}

	if (!column) return null

	const labelContent = column.header
		? column.header()
		: typeof column.label === 'function'
		? column.label()
		: column.label

	// The ds variant carries the DS TableHead sort treatment: the whole cell
	// sorts (click or Enter/Space), the label darkens on hover, the idle
	// chevrons only show while hovered, and the cell announces aria-sort.
	// The cg-* classes come from the DS stylesheet, so the hover and focus
	// colors stay the DS tokens. The default variant keeps the icon-only
	// click it always had.
	const isSortable = isDs && !!onSort && !!column.sortKey
	const sortDirection =
		sort === column.sortKey
			? 'asc'
			: sort === `-${column.sortKey}`
			? 'desc'
			: null

	// `tableLayout: fixed` reads every column width from the FIRST row of the
	// table, and that row is this header row. A width written only on the body
	// cell therefore pins nothing: the browser keeps re-dividing the columns
	// from the rows the virtualiser has mounted, so the cells change width while
	// the user scrolls. The width has to sit on the header cell as well.
	const { key, ...headerProps }: any = reactColumn.getHeaderProps()
	const widthStyle = column.width
		? {
				width: column.width,
				minWidth: column.width,
				maxWidth: column.width
		  }
		: undefined

	return (
		<th
			key={key}
			{...headerProps}
			style={{ ...(headerProps.style || {}), ...(widthStyle || {}) }}
			onClick={isSortable ? () => raiseSort(column) : undefined}
			onKeyDown={
				isSortable
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								raiseSort(column)
							}
					  }
					: undefined
			}
			tabIndex={isSortable ? 0 : undefined}
			aria-sort={
				sortDirection === 'asc'
					? 'ascending'
					: sortDirection === 'desc'
					? 'descending'
					: undefined
			}
			className={twMerge(
				isDs
					? 'px-6 py-3 h-[44px] text-right font-grotesk text-[12px] leading-4 font-semibold text-[rgb(var(--cg-text-secondary))] border-b border-b-[rgb(var(--cg-border))] cg-bg-canvas cg-select-none cg-transition-colors cg-duration-150'
					: 'px-4 py-3 text-right text-sm font-medium text-t-desc-gray border-b border-b-t-checkbox',
				isSortable
					? 'cg-group cg-cursor-pointer hover:cg-text-text-primary focus-visible:cg-outline-none focus-visible:cg-shadow-focus-sm'
					: '',
				column.hidden ? 'hidden' : '',
				column.mobileHide ? 'hidden md:table-cell' : '',
				column.tabletHide ? 'hidden lg:table-cell' : '',
				column.parentClassName || '',
				column.freeze ? 'sticky left-0' : '',
				column.freezeRight ? 'sticky right-0 z-20' : '',
				!isDs && column.freezeRight && isScrolling
					? 'shadow-[-8px_0_12px_-8px_rgba(16,24,40,0.12)]'
					: '',
				isScrolling ? (isDs ? 'cg-bg-canvas' : 'bg-white') : ''
			)}
		>
			<div
				className={twMerge(
					isDs
						? 'flex w-full h-full justify-end items-center cg-gap-1'
						: 'flex w-full text-sm h-full px-2 justify-end items-center',
					column.className || ''
				)}
			>
				{isDs && column.info ? (
					/* The DS Table CustomRules recipe's info head cell, verbatim. The
					   recipe's arbitrary cg- classes are story-build JIT output, so the
					   vendored stylesheet lacks them; those few are respelled to the
					   platform's own Tailwind, which compiles the identical CSS. The
					   stopPropagation keeps a click or Enter on the (i) from sorting
					   the column. */
					<span className="cg-inline-flex cg-items-center cg-gap-1 cg-align-middle">
						<span className="cg-whitespace-nowrap">{labelContent}</span>
						<Tooltip
							content={column.info.title}
							description={column.info.description}
							position="top"
							showArrow
							portal
						>
							<span
								tabIndex={0}
								role="img"
								aria-label={column.info.title}
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}
								className="cg-flex cg-h-3 cg-w-3 cg-shrink-0 cg-cursor-default cg-items-center cg-justify-center cg-rounded-full text-[rgb(var(--cg-icon-secondary)_/_0.7)] cg-transition-colors hover:text-[rgb(var(--cg-icon-secondary))] focus-visible:cg-shadow-focus-sm focus-visible:cg-outline-none [&>svg]:h-3 [&>svg]:w-3"
							>
								<InfoCircle />
							</span>
						</Tooltip>
					</span>
				) : (
					labelContent
				)}
				{column.sortKey && !isDs && (
					<span
						className="cursor-pointer mx-2"
						onClick={() => raiseSort(column)}
					>
						{renderSortIcon(column)}
					</span>
				)}
				{isSortable && sortDirection === 'asc' && (
					<ChevronUp className="cg-h-4 cg-w-4 cg-shrink-0" aria-hidden="true" />
				)}
				{isSortable && sortDirection === 'desc' && (
					<ChevronDown
						className="cg-h-4 cg-w-4 cg-shrink-0"
						aria-hidden="true"
					/>
				)}
				{isSortable && sortDirection === null && (
					<ChevronsUpDown
						className="cg-h-4 cg-w-4 cg-shrink-0 cg-opacity-0 group-hover:cg-opacity-40"
						aria-hidden="true"
					/>
				)}
			</div>
			{isDs && column.freeze && (
				<StickyEdgeShadow show={!!isScrolling} side="right" />
			)}
			{isDs && column.freezeRight && <StickyEdgeShadow show={!isAtEnd} />}
		</th>
	)
}
